
import { useState, useRef, useEffect } from 'react';

interface UsePublishTimerProps {
  interval: number;
  isActive: boolean;
}

export function usePublishTimer({ interval, isActive }: UsePublishTimerProps) {
  const [timeUntilNextPublish, setTimeUntilNextPublish] = useState(interval);
  const autoPublishTimerRef = useRef<number | null>(null);
  const countdownTimerRef = useRef<number | null>(null);
  
  // Start countdown timer using setTimeout instead of setInterval for better reliability
  const startCountdownTimer = () => {
    console.log('Starting countdown timer');
    // Reset the countdown to the full interval duration
    setTimeUntilNextPublish(interval);
    
    // Clear any existing timer first
    if (countdownTimerRef.current) {
      window.clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    
    // Define recursive function to update the countdown
    const tick = () => {
      setTimeUntilNextPublish(prev => {
        const newValue = prev <= 1 ? interval : prev - 1;
        
        // If we've reached the end of the countdown, reset to interval
        if (prev <= 1) {
          console.log('Countdown complete, resetting to full interval:', interval);
        }
        
        // Set up the next tick if still active
        if (isActive) {
          countdownTimerRef.current = window.setTimeout(tick, 1000);
        }
        
        return newValue;
      });
    };
    
    // Start the countdown
    countdownTimerRef.current = window.setTimeout(tick, 1000);
  };

  // Reset auto-publish timer and countdown
  const resetTimer = () => {
    // Clear existing timers
    if (autoPublishTimerRef.current) {
      console.log('Clearing existing auto-publish timer');
      window.clearTimeout(autoPublishTimerRef.current);
      autoPublishTimerRef.current = null;
    }
    
    if (countdownTimerRef.current) {
      console.log('Clearing existing countdown timer');
      window.clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    
    // Reset countdown to match the new interval
    setTimeUntilNextPublish(interval);
    
    // If auto-publish is enabled, restart the timer
    if (isActive) {
      console.log(`Restarting auto-publish timer with interval of ${interval} seconds`);
      autoPublishTimerRef.current = null; // Clear the reference to trigger setup in the effect
      startCountdownTimer();
    }
  };
  
  // Cleanup function to clear timers
  const clearTimers = () => {
    if (autoPublishTimerRef.current) {
      window.clearTimeout(autoPublishTimerRef.current);
      autoPublishTimerRef.current = null;
    }
    
    if (countdownTimerRef.current) {
      window.clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  };
  
  // Cleanup on unmount or when dependencies change
  useEffect(() => {
    return () => {
      console.log('Cleaning up publish timer on unmount');
      clearTimers();
    };
  }, []);
  
  return {
    timeUntilNextPublish,
    autoPublishTimerRef,
    startCountdownTimer,
    resetTimer,
    clearTimers
  };
}
